import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button } from './ui.js';
import { useBuilds } from '../api/builds.js';
import { useAskDebug, useDraftStory } from '../api/debug.js';
import { useCreateStory } from '../api/projects.js';

const fieldClass =
  'rounded-md border border-secondary/20 bg-white px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary';

/**
 * Per-project debug: ask Claude about this one project, or describe a bug/change
 * and turn it into a user story. The project is fixed by the surrounding page,
 * so there is no project picker — just an optional build selector and the text.
 * Claude sees this project's spec, stories, the selected build's transcript, and
 * its deploy status.
 */
export function DebugPanel({ projectId }: { projectId: string }) {
  const [buildId, setBuildId] = useState('');
  const [prompt, setPrompt] = useState('');
  const builds = useBuilds(projectId);
  const ask = useAskDebug();
  const draft = useDraftStory(projectId);
  const create = useCreateStory(projectId);

  const buildOpt = buildId ? { buildId } : {};
  const canAct = prompt.trim().length > 0;
  const drafted = draft.data?.story;

  return (
    <div className="flex flex-col gap-3">
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">Build</span>
        <select className={fieldClass} value={buildId} onChange={(e) => setBuildId(e.target.value)}>
          <option value="">Most recent build</option>
          {(builds.data ?? []).map((b) => (
            <option key={b.id} value={b.id}>
              {b.status} · {new Date(b.createdAt).toLocaleString()} · {b.id.slice(0, 8)}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">Question, or a bug/change to turn into a story</span>
        <textarea
          className={`min-h-24 ${fieldClass}`}
          placeholder="e.g. The test didn’t work — what went wrong? · or: The login button does nothing on mobile."
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
        />
      </label>

      <div className="flex flex-wrap gap-2">
        <Button
          disabled={!canAct || ask.isPending}
          onClick={() => ask.mutate({ projectId, ...buildOpt, prompt: prompt.trim() })}
        >
          {ask.isPending ? 'Asking Claude…' : 'Ask Claude'}
        </Button>
        <Button
          variant="secondary"
          disabled={!canAct || draft.isPending}
          onClick={() => {
            create.reset();
            draft.mutate({ prompt: prompt.trim(), ...buildOpt });
          }}
          title="Draft a user story to fix or implement what you described"
        >
          {draft.isPending ? 'Drafting story…' : 'Draft a user story'}
        </Button>
      </div>

      {ask.isError ? (
        <Alert>
          Could not get an answer. The assistant may be unavailable, or the build could not be
          found.
        </Alert>
      ) : null}

      {ask.data ? (
        <div className="rounded-md border border-secondary/20 bg-neutral/40 p-4">
          <p className="whitespace-pre-wrap text-sm leading-relaxed">{ask.data.answer}</p>
          <p className="mt-3 text-xs opacity-60">
            {ask.data.model} · {ask.data.usage.inputTokens + ask.data.usage.outputTokens} tokens
            {ask.data.usedBuildId ? ` · build ${ask.data.usedBuildId.slice(0, 8)}` : ''}
            {ask.data.toolCalls ? ` · ${ask.data.toolCalls} AWS check(s)` : ''}
          </p>
        </div>
      ) : null}

      {draft.isError ? <Alert>Could not draft a story. Try rephrasing the request.</Alert> : null}

      {drafted ? (
        <div className="rounded-md border-2 border-primary/30 bg-primary/5 p-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">
            Drafted user story
          </p>
          <h3 className="font-medium">{drafted.title}</h3>
          <p className="mt-1 text-sm leading-relaxed">
            As <em>{drafted.asA}</em>, I want {drafted.iWant}, so that {drafted.soThat}.
          </p>
          {drafted.acceptanceCriteria.length > 0 ? (
            <ul className="mt-2 list-disc space-y-0.5 pl-5 text-sm opacity-80">
              {drafted.acceptanceCriteria.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          ) : null}

          {create.isSuccess ? (
            <p className="mt-3 border-t border-primary/20 pt-3 text-sm font-medium">
              Added as story #{create.data.sequence}.{' '}
              <Link
                to={`/console/projects/${projectId}?section=stories&story=${create.data.id}`}
                className="text-primary underline"
              >
                View it in User stories →
              </Link>
            </p>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2 border-t border-primary/20 pt-3">
              <Button
                disabled={create.isPending}
                onClick={() =>
                  create.mutate({
                    title: drafted.title,
                    asA: drafted.asA,
                    iWant: drafted.iWant,
                    soThat: drafted.soThat,
                    acceptanceCriteria: drafted.acceptanceCriteria,
                  })
                }
              >
                {create.isPending ? 'Adding…' : 'Add to backlog'}
              </Button>
              <Button variant="secondary" onClick={() => draft.reset()}>
                Discard
              </Button>
            </div>
          )}

          {create.isError ? (
            <Alert>
              Could not add the story — the project needs an approved specification first.
            </Alert>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
